// ============================================
// EVENT CONTEXT HELPERS
// Manages the "current active event" that scopes all event-specific data.
// Default legacy event is Medsparkz.
// ============================================

import { supabase } from './supabase';
import type { MedicalEvent, User } from '../types';

export const DEFAULT_EVENT_ID = 'medsparkz26';
const ACTIVE_EVENT_KEY = 'active_event_id';

/** Returns the currently active event id. Defaults to the legacy Medsparkz event. */
export const getActiveEventId = (): string => {
  return localStorage.getItem(ACTIVE_EVENT_KEY) || DEFAULT_EVENT_ID;
};

/** Sets the active event id (used when switching/opening events). */
export const setActiveEventId = (eventId: string): void => {
  localStorage.setItem(ACTIVE_EVENT_KEY, eventId);
  try {
    window.dispatchEvent(new CustomEvent('active-event-changed', { detail: eventId }));
  } catch (_e) { /* ignore */ }
};

/** Clears the active event id (e.g. during logout). */
export const clearActiveEvent = (): void => {
  localStorage.removeItem(ACTIVE_EVENT_KEY);
};

/** Resolve the user's default event context after login. */
export const resolveUserEventContext = (user: User): void => {
  // Master admin has no default event restriction; leave existing context (defaults to Medsparkz).
  if (user.role === 'master_admin') return;

  const eventIds = user.eventIds?.length ? user.eventIds : [DEFAULT_EVENT_ID];
  setActiveEventId(eventIds[0]);
};

/** Fetch the events a user can access (via user_events junction). */
export const getEventsForUser = async (userId: string): Promise<MedicalEvent[]> => {
  const { data, error } = await supabase
    .from('user_events')
    .select('event_id')
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching user events:', error);
    return [];
  }

  const eventIds = (data || []).map((r: Record<string, unknown>) => r.event_id as string);
  if (eventIds.length === 0) return [];

  const { data: events, error: eventError } = await supabase
    .from('events')
    .select('*')
    .in('id', eventIds);

  if (eventError || !events) {
    console.error('Error fetching events:', eventError);
    return [];
  }

  const statusOrder = ['active', 'completed', 'draft', 'archived'];
  return (events as MedicalEvent[]).sort(
    (a, b) => statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status)
  );
};

/** Validate that a user may access the given event (server-side style check). */
export const canAccessEvent = (user: User, eventId: string): boolean => {
  if (user.role === 'master_admin') return true;
  return (user.eventIds || []).includes(eventId);
};